import React, { Component } from 'react';
import {
  ArrayInput,
  BooleanInput,
  maxLength,
  maxValue,
  minLength,
  minValue,
  number,
  NumberInput,
  ReferenceArrayInput,
  ReferenceInput,
  AutocompleteArrayInput,
  required,
  SelectInput,
  SimpleFormIterator,
  TabbedForm,
  TextInput,
} from 'react-admin';
import Button from '@material-ui/core/Button';
import PriceInput from '../PriceInput';
import ImageUploadSelector from '../ImageUploadSelector';
import { ZippityFormTab } from '../../components';
import { carMakeOptions } from '../../VehicleConstants';
import ReferenceChild from '../../components/ReferenceChild';
import ReferenceArrayChild from '../../components/ReferenceArrayChild';
import { perPage } from '../../helper';

const SELECT_INPUT_CHOICES = {
  maintenance_repair_category: [
    { id: 'maintenance', name: 'Maintenance' },
    { id: 'repair', name: 'Repair' },
    { id: 'cleaning', name: 'Cleaning' },
    { id: 'other', name: 'Other' },
  ],
  tax_type: [
    { id: 'service', name: 'Service' },
    { id: 'itemized', name: 'Itemized' },
    { id: 'bundled', name: 'Bundled' },
  ],
  display_category: [
    { id: 'package', name: 'Package' },
    { id: 'standard', name: 'Standard' },
    { id: 'addon', name: 'Addon' },
  ],
};

export default class TabForm extends Component {
  constructor(props) {
    super(props);
    this.validateName = [required(), minLength(2), maxLength(255)];
    this.validateLaborHours = [number(), minValue(0), maxValue(500)];
    this.validateDisplayOrder = [required(), number()];

    this.state = {
      shortOrTinyName: null,
      upsellableOnly: false,
    };
  }

  renderSelectInput = (source, isHidden = false) => {
    const [defaultValue, label] = (() => {
      switch (source) {
        case 'tax_type':
          return ['itemized', null];
        case 'maintenance_repair_category':
          return ['repair', null];
        case 'display_category':
          return [null, 'Display: Category'];
        default:
          return [null, null];
      }
    })();
    const labelParam = label ? { label } : null;

    return (
      <SelectInput
        source={source}
        {...labelParam}
        validate={[required()]}
        choices={SELECT_INPUT_CHOICES[source]}
        style={isHidden ? { display: 'none' } : null}
        defaultValue={defaultValue}
      />
    );
  };

  copyLongNameIfNull = (e) => {
    this.setState({ shortOrTinyName: e.target.value });
  };

  handleUpsellableOnlyChange = () => {
    const { upsellableOnly } = this.state;
    this.setState({ upsellableOnly: !upsellableOnly });
  };

  render() {
    const { record, resource, classes, create } = this.props;
    const { shortOrTinyName, upsellableOnly } = this.state;
    const upsellStyle = upsellableOnly ? { display: 'none' } : {};

    return (
      <TabbedForm record={record} resource={resource} {...this.props}>
        <ZippityFormTab label="resources.service-offering.tabs.basics">
          <br />
          {create && (
            <Button
              variant="contained"
              color="primary"
              style={{
                border: '1px solid #283493',
                borderRadius: '5px',
              }}
              onClick={this.handleUpsellableOnlyChange}
            >
              Upsell Quick Fill
            </Button>
          )}
          <TextInput
            multiline
            validate={this.validateName}
            source="long_name"
            onChange={this.copyLongNameIfNull}
          />
          <TextInput
            multiline
            validate={this.validateName}
            source="short_name"
            defaultValue={shortOrTinyName}
          />
          <TextInput
            multiline
            validate={this.validateName}
            source="tiny_internal_name"
            defaultValue={shortOrTinyName}
          />
          <ReferenceArrayInput
            source="schedule_type_ids"
            reference="schedule-type"
            perPage={perPage}
            className={classes.referenceArrayWide}
            sort={{ field: 'name', order: 'ASC' }}
          >
            <ReferenceArrayChild
              {...this.props}
              optionText="name"
              source="schedule_type_ids"
            />
          </ReferenceArrayInput>
          <NumberInput
            validate={this.validateLaborHours}
            source="labor_hours"
          />
          <AutocompleteArrayInput
            source="offered_vehicle_makes"
            fullWidth
            choices={carMakeOptions}
          />
          <TextInput multiline source="marketing_url" />
          <ReferenceInput
            source="parent_service_offering_id"
            reference="service-offering"
            sort={{ field: 'short_name', order: 'ASC' }}
            perPage={perPage}
            allowEmpty
          >
            <ReferenceChild
              {...this.props}
              optionText="short_name"
              source="parent_service_offering_id"
            />
          </ReferenceInput>
          <BooleanInput
            validate={[required()]}
            source="bookable"
            defaultValue={false}
            style={upsellStyle}
          />
          <BooleanInput
            validate={[required()]}
            source="upsellable"
            defaultValue={upsellableOnly}
            style={upsellStyle}
          />
          <BooleanInput
            source="add_oil_cost"
            defaultValue={false}
            style={upsellStyle}
          />
          <BooleanInput
            source="included_with_every_mechanical_service"
            defaultValue={false}
            style={upsellStyle}
          />
          {upsellableOnly && this.renderSelectInput('tax_type')}
          {upsellableOnly &&
            this.renderSelectInput('maintenance_repair_category', true)}
        </ZippityFormTab>
        {!upsellableOnly && (
          <ZippityFormTab label="resources.service-offering.tabs.pricing">
            <PriceInput source="original_value_dollars" />
            {this.renderSelectInput('tax_type')}
            {this.renderSelectInput('maintenance_repair_category')}
            <BooleanInput source="variable_pricing" style={upsellStyle} />
            <BooleanInput source="per_tire" />
          </ZippityFormTab>
        )}
        {!upsellableOnly && (
          <ZippityFormTab label="resources.service-offering.tabs.display">
            <NumberInput
              validate={this.validateDisplayOrder}
              source="display_order"
            />
            <TextInput source="display_superscript" />
            {this.renderSelectInput('display_category')}
            <BooleanInput source="display_emphasize" />

            <ImageUploadSelector source="card_image_url" folder="booking-app" />

            <ReferenceArrayInput
              source="incompatible_service_offering_ids"
              reference="service-offering"
              sort={{ field: 'long_name', order: 'ASC' }}
              perPage={perPage}
              className={classes.referenceArrayWide}
            >
              <ReferenceArrayChild
                {...this.props}
                optionText="long_name"
                source="incompatible_service_offering_ids"
              />
            </ReferenceArrayInput>

            <ReferenceInput
              allowEmpty
              source="custom_message_id_1"
              reference="service-custom-message"
              sort={{ field: 'internal_name', order: 'ASC' }}
              perPage={perPage}
            >
              <ReferenceChild
                {...this.props}
                optionText="internal_name"
                source="custom_message_id_1"
              />
            </ReferenceInput>
            <ReferenceInput
              allowEmpty
              source="custom_message_id_2"
              reference="service-custom-message"
              sort={{ field: 'internal_name', order: 'ASC' }}
              perPage={perPage}
            >
              <ReferenceChild
                {...this.props}
                optionText="internal_name"
                source="custom_message_id_2"
              />
            </ReferenceInput>
          </ZippityFormTab>
        )}
        {!upsellableOnly && (
          <ZippityFormTab label="resources.service-offering.tabs.card">
            <h5 style={{ width: '100%' }}>
              *This is only displayed for a bookable, detailing or tire service*
              <br />
              *The title comes from the short name*
            </h5>
            <TextInput multiline source="subtitle" />
            <ImageUploadSelector
              source="inline_image_url"
              folder="booking-app"
            />
            <br />
            <hr style={{ width: '100%' }} />
            <h3>More Info:</h3>
            <ArrayInput source="card_bullet_points" style={{ width: '100%' }}>
              <SimpleFormIterator>
                <TextInput
                  multiline
                  label="resources.service-offering.fields.bullet"
                />
              </SimpleFormIterator>
            </ArrayInput>

            <TextInput multiline source="details_blurb" />
            <ImageUploadSelector
              source="more_info_image"
              folder="booking-app"
            />
          </ZippityFormTab>
        )}
      </TabbedForm>
    );
  }
}
